import {inject, Injectable, NgZone} from '@angular/core';
import {Platform} from "@ionic/angular/standalone";
import {Capacitor} from "@capacitor/core";
import {ErrorService} from "../error/error.service";
import {BehaviorSubject, combineLatest} from "rxjs";
import {AuthService} from "../auth/auth.service";
import {LoggedInUser} from "../auth/logged-in-user.model";
import {SpinnerService} from "../spinner/spinner.service";
import {LoggerService} from "../logging/logger.service";
import {
  ObservabilityLevel,
  ObservabilityService
} from "../observability/observability.service";
import {distinctUntilChanged, map} from "rxjs/operators";
import {Router} from "@angular/router";
import {
  CustomerInfo,
  PURCHASE_PROVIDER,
  PurchaseProvider,
  SubscriptionPackage,
  SubscriptionProduct
} from './purchase-provider.interface';
import {UserContextService} from
  '../userContext/user-context.service';
import {PostHogService} from
  '../posthog/posthog.service';


@Injectable({
  providedIn: 'root'
})
export class SubscriptionService {
  private readonly platform = inject(Platform);
  private readonly errorService = inject(ErrorService);
  private readonly authService = inject(AuthService);
  private readonly spinnerService =
    inject(SpinnerService);
  private readonly logger = inject(LoggerService);
  private readonly observabilityService =
    inject(ObservabilityService);
  private readonly router = inject(Router);
  private readonly zone = inject(NgZone);
  private readonly provider: PurchaseProvider =
    inject(PURCHASE_PROVIDER);
  private readonly userContextService =
    inject(UserContextService);
  private readonly postHogService =
    inject(PostHogService);

  private customerInfo?: CustomerInfo;
  private listenerRegistered = false;

  private readonly isSubscribedSubject =
    new BehaviorSubject<boolean>(false);
  public isSubscribed$ =
    this.isSubscribedSubject.asObservable();

  public hasAccess$ = combineLatest([
    this.isSubscribed$,
    this.userContextService.currentUser$
  ]).pipe(
    map(([sub]) => this.userContextService
      .hasEffectiveAccess(sub)),
    distinctUntilChanged()
  );

  public get isSubscribed(): boolean {
    return this.isSubscribedSubject.getValue();
  }

  private readonly initialisedSubject =
    new BehaviorSubject<boolean>(false);
  public initialised$ =
    this.initialisedSubject.asObservable();

  private readonly offeringsSubject =
    new BehaviorSubject<SubscriptionPackage[] | null>(
      null
    );
  public offerings$ =
    this.offeringsSubject.asObservable();

  loggedInUser: LoggedInUser | null = null;

  constructor() {
    this.isSubscribed$
      .pipe(distinctUntilChanged())
      .subscribe(value =>
        this.userContextService.setSubscribed(value));

    combineLatest([
      this.authService.initialised$,
      this.authService.loggedInUser$,
    ]).pipe(
      map(async ([authInitialised, user]) => {
        // only proceed if auth is initialised
        if (authInitialised) {
          // then we know this is the good state of
          // the user...
          if (user && !this.loggedInUser) {
            this.loggedInUser = user;
            this.logger.info(
              'SubscriptionService detected '
              + 'logged in user:',
              user.getSub()
            );
            this.initialiseUser(
              this.loggedInUser.getSub()
            ).then();
          } else if (this.loggedInUser && !user) {
            this.logger.info(
              'User logged out, resetting '
              + 'SubscriptionService state'
            );
            this.loggedInUser = null;
          } else {
            // we're not logged in, so mark as
            // initialised
            this.logger.info(
              'no change, just marking '
              + 'SubscriptionService as initialised'
            );
            this.initialisedSubject.next(true);
          }
        }
    })).subscribe();
  }

  public async initialiseUser(
    appUserId: string,
    forceLoadOfferings: boolean = false
  ) {
    this.logger.info(
      'Platform is', Capacitor.getPlatform(),
      'Initializing Purchases with appUserId:',
      appUserId
    );

    await this.platform.ready();

    try {
      if (!this.isCurrentPlatformSupported()) {
        this.zone.run(
          () => this.isSubscribedSubject.next(true)
        );
        this.logger.info(
          'Platform not supported, '
          + 'auto-subscribing'
        );
        this.observabilityService
          .trackServiceInitialized(
            'SubscriptionService',
            {
              platform: 'web',
              isSubscribed: this.isSubscribed,
              platformSupported: false
            }
          );
        this.initialisedSubject.next(true);
        return;
      }

      // Check if already configured
      const isConfigured =
        await this.provider.isConfigured();

      if (isConfigured) {
        this.logger.debug(
          'Purchases already configured, '
          + 'calling login with appUserId: '
          + appUserId
        );
        await this.provider.logIn(appUserId);
        this.provider.initialiseAttribution();
        await this.addCustomerInfoListener();
        await this.loadOfferings();
        this.initialisedSubject.next(true);
      } else {
        // Configure Purchases for the first time
        await this.configurePurchases(appUserId);
      }

      if (forceLoadOfferings) {
        // Force a reload of the offerings
        await this.loadOfferings();
      }

      this.observabilityService
        .trackServiceInitialized(
          'SubscriptionService',
          {
            platform: Capacitor.getPlatform(),
            appUserId: appUserId,
            isSubscribed: this.isSubscribed,
            platformSupported: true
          }
        );

    } catch (error) {
      this.logger.error(
        'Error during initialization:', error
      );
      this.observabilityService.captureException(
        error,
        {
          context:
            'subscription_service_initialization',
          platform: Capacitor.getPlatform(),
          appUserId: appUserId ?? 'anonymous',
          isCurrentPlatformSupported:
            this.isCurrentPlatformSupported(),
          service: 'SubscriptionService'
        }
      );
      this.errorService.registerError(error);
      // Still mark as initialised to prevent blocking
      this.initialisedSubject.next(true);
    }
  }

  private async configurePurchases(
    appUserId: string
  ): Promise<void> {
    this.logger.info(
      '🚀 Starting RevenueCat configuration...'
    );

    await this.provider.setLogLevel('error');

    this.logger.debug('📝 Configuration details:', {
      platform: Capacitor.getPlatform(),
      appUserId: appUserId || 'none'
    });

    await this.provider.configure({
      appUserID: appUserId
    });

    this.logger.info(
      '✅ RevenueCat configuration completed'
    );

    this.provider.initialiseAttribution();

    await this.addCustomerInfoListener();
  }

  private async addCustomerInfoListener():
    Promise<void> {
    if (this.listenerRegistered) {
      this.logger.debug(
        'Customer info listener already '
        + 'registered, skipping'
      );
      return;
    }
    this.listenerRegistered = true;

    await this.provider
      .addCustomerInfoUpdateListener(
        async (customerInfo) => {
          try {
            this.customerInfo = customerInfo;

            this.logger.info(
              '🔔 Customer info update received: ',
              this.logger.stringify(customerInfo)
            );

            this.zone.run(() =>
              this.isSubscribedSubject.next(
                !!customerInfo
                  .entitlements.active['basic']
              )
            );

            // Log to Sentry for debugging
            // subscription status issues
            this.observabilityService
              .trackCustomerInfoReceived(
                this.loggedInUser?.getSub(),
                this.isSubscribed,
                {
                  entitlements: Object.keys(
                    customerInfo.entitlements.active
                  ),
                  allEntitlements: Object.keys(
                    customerInfo.entitlements.all
                  ),
                  originalPurchaseDate:
                    customerInfo.originalPurchaseDate,
                  latestExpirationDate:
                    customerInfo
                      .latestExpirationDate,
                  platform:
                    Capacitor.getPlatform()
                }
              );

            this.logger.info(
              'Subscription status: '
              + `${this.isSubscribed
                ? 'Subscribed'
                : 'Not Subscribed'}`
            );

            if (this.isSubscribed) {
              this.logger.info(
                '🔄 User subscribed, waiting for '
                + 'subscribed roles to load'
              );
              this.authService
                .waitForSubscribedRoles();
            } else {
              if (this.userContextService
                .isInFreeTrial()) {
                this.logger.info(
                  'User not subscribed but in '
                  + 'free trial, skipping role '
                  + 'polling'
                );
              } else {
                this.logger.info(
                  'User not subscribed and trial '
                  + 'expired, loading offerings'
                );
                await this.loadOfferings();
                this.authService
                  .waitForUnSubscribedRoles();
                const onLanding = this.router.url
                  .startsWith('/landing');
                if (!onLanding) {
                  this.logger.info(
                    'Navigating to home page '
                    + 'after unsubscription'
                  );
                  await this.router.navigate(
                    ['tabs', 'home']
                  );
                } else {
                  this.logger.info(
                    'On landing flow, skipping '
                    + 'home navigation'
                  );
                }
              }
            }

            this.logger.debug(
              '👤 Customer info updated:', {
                isSubscribed: this.isSubscribed,
                entitlements: Object.keys(
                  customerInfo.entitlements.active
                ),
                originalPurchaseDate:
                  customerInfo.originalPurchaseDate,
                latestExpirationDate:
                  customerInfo.latestExpirationDate
              }
            );
          } finally {
            this.initialisedSubject.next(true);
          }
        }
      );

    this.logger.info(
      '🎧 Customer info listener configured'
    );
  }

  public async loadOfferings(): Promise<void> {
    this.logger.info(
      '🔄 Starting to load offerings...'
    );
    this.spinnerService.showImmediately();
    try {
      // Check configuration before loading
      const isConfigured =
        await this.provider.isConfigured();
      this.logger.info(
        `📋 Configuration check: ${isConfigured}`
      );

      if (!isConfigured) {
        throw new Error(
          'Purchases not configured when '
          + 'attempting to load offerings'
        );
      }

      const packages =
        await this.provider.getOfferings();

      this.logger.info(
        '✅ Successfully loaded '
        + `${packages.length} offerings`
      );

      // Log offering details for debugging
      packages.forEach((pkg, index) => {
        const product = pkg.product;
        const hasFreeTrial =
          product.introPrice?.price === 0;
        const trialPeriod =
          product.introPrice?.period ?? 'none';

        this.logger.debug(
          `📦 Package ${index + 1}: `
          + pkg.identifier,
          {
            productId: product.identifier,
            price: product.priceString,
            monthlyPrice:
              product.pricePerMonthString,
            hasFreeTrial,
            trialPeriod,
            subscriptionPeriod:
              product.subscriptionPeriod
          }
        );
      });

      if (packages.length > 0) {
        this.observabilityService
          .trackSubscriptionsOffered(packages, {
            platform: Capacitor.getPlatform(),
            offeringCount: packages.length,
            userId: this.loggedInUser?.getSub(),
            source: 'loadOfferings'
          });
      } else {
        this.logger.warn(
          '⚠️ No packages available '
          + '- check RevenueCat configuration'
        );
        this.observabilityService
          .trackSubscriptionDataAnomaly(
            'no_offerings_available',
            'Subscription component initialized '
            + 'with no offerings',
            ObservabilityLevel.WARNING,
            { offeringsCount: 0 }
          );
      }

      this.offeringsSubject.next(packages);
    } catch (error) {
      this.logger.error(
        '❌ Error loading offerings:',
        this.logger.stringify(error)
      );
      this.observabilityService.captureException(
        error,
        {
          context: 'load_offerings',
          platform: Capacitor.getPlatform(),
          userId: this.loggedInUser?.getSub(),
          service: 'SubscriptionService',
          error: error instanceof Error
            ? error.message : 'Unknown error'
        }
      );
      this.errorService.registerError(error);
      // Emit empty array on error
      this.offeringsSubject.next([]);
      this.initialisedSubject.next(true);
    } finally {
      this.spinnerService.hide();
      this.logger.info(
        '🔚 Finished loading offerings'
      );
    }
  }

  private readonly supportedPlatforms =
    ['ios', 'android', 'web'];

  private isCurrentPlatformSupported(): boolean {
    return this.supportedPlatforms.includes(
      Capacitor.getPlatform()
    );
  }

  isManagementAvailable(): boolean {
    return this.provider.isManagementAvailable()
      && this.isSubscribed;
  }

  async openManagementPage(): Promise<void> {
    await this.provider.openManagementPage();
  }

  isRedeemCodeAvailable(): boolean {
    return this.provider.isRedeemCodeAvailable();
  }

  async redeemCode(): Promise<void> {
    this.observabilityService
      .trackRedeemCodeOpened({
        userId: this.loggedInUser?.getSub(),
        platform: Capacitor.getPlatform()
      });
    try {
      await this.provider.redeemCode();
    } catch (error: any) {
      this.logger.error(
        'Error opening redeem code flow:',
        this.logger.stringify(error)
      );
      this.observabilityService
        .trackRedeemCodeFailed(
          error instanceof Error
            ? error.message : 'Unknown error',
          {
            userId: this.loggedInUser?.getSub(),
            platform: Capacitor.getPlatform()
          }
        );
      throw error;
    }
  }

  private async arePurchasesReady(): Promise<boolean> {
    try {
      return await this.provider.isConfigured();
    } catch (error) {
      this.logger.error(
        'Error checking Purchases configuration:',
        error
      );
      return false;
    }
  }

  public async buy(
    offering: SubscriptionProduct
  ) {
    try {
      this.spinnerService.showImmediately();

      const offer = offering.effectiveOffer;

      // Track purchase attempt
      this.observabilityService
        .trackPurchaseAttempted(
          offering.identifier,
          {
            platform: Capacitor.getPlatform(),
            productType: offering.productType,
            price: offering.price,
            priceString: offering.priceString,
            currencyCode: offering.currencyCode,
            userId: this.loggedInUser?.getSub(),
            source: 'buy_method',
            offering_id: offering.offeringIdentifier,
            offer_kind: offer?.kind,
            offer_price: offer?.priceString,
            intro_eligibility: offer?.eligibility
          }
        );

      const purchasesReady =
        await this.arePurchasesReady();
      if (purchasesReady) {
        this.logger.debug(
          'Buying offering:',
          this.logger.stringify(offering)
        );
        const email =
          this.loggedInUser?.userInfo?.['email'];
        const purchaseResult =
          await this.provider.purchase(
            offering, { customerEmail: email }
          );

        // Track successful purchase
        if (typeof purchaseResult
          .customerInfo.entitlements
          .active['basic'] !== 'undefined') {

          this.postHogService.capture(
            'user_subscribed', {
              product: offering.identifier,
              price: offering.price,
              currency: offering.currencyCode,
              offering_id: offering.offeringIdentifier,
              offer_kind: offer?.kind,
              offer_price: offer?.priceString,
              intro_eligibility: offer?.eligibility
            }
          );
          this.observabilityService
            .trackPurchaseSuccess(
              offering.identifier,
              purchaseResult
                .customerInfo
                .originalPurchaseDate ?? undefined,
              {
                platform:
                  Capacitor.getPlatform(),
                productType: offering.productType,
                price: offering.price,
                priceString: offering.priceString,
                currencyCode:
                  offering.currencyCode,
                userId:
                  this.loggedInUser?.getSub(),
                entitlements: Object.keys(
                  purchaseResult
                    .customerInfo
                    .entitlements.active
                ),
                source: 'buy_method',
                offering_id: offering.offeringIdentifier,
                offer_kind: offer?.kind,
                offer_price: offer?.priceString,
                intro_eligibility: offer?.eligibility
              }
            );
        }
      }
    } catch (error: any) {
      // Detect and track user cancellations
      // separately from failures.
      // Web SDK: errorCode === 1
      // Capacitor: message contains 'cancelled'
      const isUserCancellation =
        error
        && typeof error === 'object'
        && (error.errorCode === 1
          || error?.message?.includes('cancelled'));
      if (isUserCancellation) {
        // Track user cancellation
        this.observabilityService
          .trackPurchaseCancelled(
            offering.identifier,
            {
              platform: Capacitor.getPlatform(),
              productType: offering.productType,
              price: offering.price,
              priceString: offering.priceString,
              currencyCode: offering.currencyCode,
              userId:
                this.loggedInUser?.getSub(),
              source: 'buy_method'
            }
          );
        this.logger.info(
          'Purchase cancelled by user:',
          offering.identifier
        );
        return;
      }

      // Track actual purchase failures
      this.observabilityService
        .trackPurchaseFailed(
          offering.identifier,
          error.message
            ?? 'Unknown purchase error',
          {
            platform: Capacitor.getPlatform(),
            productType: offering.productType,
            price: offering.price,
            priceString: offering.priceString,
            currencyCode: offering.currencyCode,
            userId: this.loggedInUser?.getSub(),
            errorCode: error.code,
            source: 'buy_method'
          }
        );

      this.logger.error(
        'Error buying offering:',
        this.logger.stringify(error)
      );
      this.errorService.registerError(error, true);
    } finally {
      this.spinnerService.hide();
    }
  }

  public async restorePurchases(): Promise<boolean> {
    this.logger.info(
      '🔄 Starting restore purchases...'
    );
    this.spinnerService.showImmediately();

    this.observabilityService
      .trackRestorePurchasesAttempted({
        userId: this.loggedInUser?.getSub(),
        platform: Capacitor.getPlatform()
      });

    try {
      const purchasesReady =
        await this.arePurchasesReady();
      if (!purchasesReady) {
        throw new Error(
          'Purchases not configured when '
          + 'attempting to restore'
        );
      }

      const result =
        await this.provider.restorePurchases();
      const isSubscribed =
        !!result.customerInfo
          .entitlements.active['basic'];
      this.zone.run(
        () => this.isSubscribedSubject
          .next(isSubscribed)
      );

      this.logger.info(
        '✅ Restore purchases completed '
        + `- Subscribed: ${isSubscribed}`
      );

      this.observabilityService
        .trackRestorePurchasesSuccess({
          userId: this.loggedInUser?.getSub(),
          isSubscribed,
          entitlements: Object.keys(
            result.customerInfo
              .entitlements.active
          ),
          platform: Capacitor.getPlatform()
        });

      return isSubscribed;
    } catch (error: any) {
      this.logger.error(
        '❌ Error restoring purchases:',
        this.logger.stringify(error)
      );

      this.observabilityService
        .trackRestorePurchasesFailed(
          error instanceof Error
            ? error.message : 'Unknown error',
          {
            userId: this.loggedInUser?.getSub(),
            platform: Capacitor.getPlatform(),
            errorCode: error?.code
          }
        );

      this.errorService.registerError(error, true);
      return false;
    } finally {
      this.spinnerService.hide();
    }
  }
}
